import { Box, Button, Collapse, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { PopUpMessageContext, useAccessLevelField, useApp, useCachedUser } from '@hb/react'
import { DropdownField, FieldTypes, FileDBValue, FileField, UserGroup } from '@hb/shared'
import { useCallback, useContext, useMemo, useState } from 'react'
import { movePatientFile } from '../../backend/functions/other'
import { useUserAssessments } from '../../hooks/backend/user/useUserAssessments'
import { SearchAssessments } from '../Assessments/SearchAssessments'
import { CollapseError } from '../CollapseError'
import { FileView, StandaloneInput } from '../forms/Input'
import { DefaultModal } from '../Modals/DefaultModal'
import { SearchedPatientPreview } from '../Users/SearchUsers'

const movedFileField: FileField = {
  type: FieldTypes.FILE,
  name: '',
  placeholder: 'File to Move',
}

interface MoveFileModalProps {
  isOpen: boolean
  onClose: () => void
  file: FileDBValue
  fileId: string
  // Source information
  sourceType: 'patient' | 'assessment'
  patientId: string
  sourceAccessLevel: UserGroup
  sourceAssessmentId?: string | null
  sourcePracticeId?: string | null
}

export const MoveFileModal = ({
  isOpen,
  onClose,
  file,
  fileId,
  sourceType,
  patientId,
  sourceAccessLevel,
  sourceAssessmentId,
}: MoveFileModalProps) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const { appName } = useApp()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destination selection
  // If no sourceAssessmentId, default to assessment destination
  const [destType, setDestType] = useState<'patient' | 'assessment' | null>(
    !sourceAssessmentId ? 'assessment' : null,
  )
  const [destAssessmentId, setDestAssessmentId] = useState<string | null>(null)
  const [destAccessLevel, setDestAccessLevel] = useState<UserGroup | null>(
    appName === 'providers-app' ? 'practice' : null,
  )

  // Show destination type selector only if moving from an assessment (has sourceAssessmentId)
  const showDestTypeSelector = !!sourceAssessmentId

  // Get patient data
  const { data: roleItem, loading: roleItemLoading } = useCachedUser(patientId)

  const { data: assessments, loading: loadingAssessments } = useUserAssessments(patientId, false)

  const assessmentsArray = useMemo(() => Object.entries(assessments ?? {}), [assessments])
  const hasAssessments = useMemo(() => assessmentsArray.length > 0, [assessmentsArray])

  const accessLevelField = useAccessLevelField(undefined, destType === 'patient')

  // Destination type dropdown field - update when patient changes
  const destTypeField = useMemo<DropdownField | null>(
    () => ({
      type: FieldTypes.DROPDOWN,
      placeholder: 'Select destination type',
      options: [
        { text: 'Patient Profile', id: 'patient' },
        ...(hasAssessments ? [{ text: 'Assessment', id: 'assessment' }] : []),
      ],
    }),
    [hasAssessments],
  )

  const canSubmit = useMemo(() => {
    if (!destType) return false
    if (destType === 'assessment' && !destAssessmentId) return false
    if (!destAccessLevel) return false
    return true
  }, [destType, destAssessmentId, destAccessLevel])

  const onSubmit = useCallback(async () => {
    if (!destType || !patientId || !destAccessLevel) {
      return processResponse({ error: 'Please complete all required fields' })
    }
    if (destType === 'assessment' && !destAssessmentId) {
      return processResponse({ error: 'Please select an assessment' })
    }

    setSubmitting(true)
    setError(null)
    try {
      await movePatientFile({
        patientId,
        sourceType,
        sourceFileId: fileId,
        sourceAccessLevel,
        sourceAssessmentId: sourceAssessmentId ?? null,
        destType,
        destAccessLevel,
        destAssessmentId: destAssessmentId ?? null,
      })
      setSubmitting(false)
      onClose()
      return processResponse({ success: 'File moved successfully' })
    } catch (err: any) {
      setSubmitting(false)
      const errorMessage = err?.message || 'Error occurred while moving file'
      setError(errorMessage)
      return processResponse({ error: errorMessage })
    }
  }, [
    destType,
    patientId,
    destAccessLevel,
    destAssessmentId,
    sourceType,
    fileId,
    sourceAccessLevel,
    sourceAssessmentId,
    processResponse,
    onClose,
  ])

  if (!isOpen) return null

  return (
    <DefaultModal
      isOpen={isOpen}
      overlayHeader
      onClose={onClose}
      headerProps={{
        onClick: e => {
          e.stopPropagation()
        },
      }}
      size="xl"
      scrollBehavior="inside"
      render={() => (
        <VStack align="flex-start" w="100%" bg="gray.50" p={3}>
          <Text fontFamily="Hero-New" fontWeight={500} color="gray.600" px={2}>
            Move File
          </Text>
          <Box w="100%" border="1px solid #cdcdcd" bg="white" overflow="hidden" borderRadius={6}>
            <FileView noFullView field={movedFileField} data={file} />
          </Box>

          {/* Patient selector */}
          <Flex w="100%" borderBottom="1px solid #cdcdcd" justify="space-between" pb={1}>
            <SearchedPatientPreview patient={roleItem} loading={roleItemLoading} />
          </Flex>

          <VStack px={2} w="100%">
            {/* Destination type selector - only show if moving from an assessment */}
            {showDestTypeSelector && (
              <Collapse style={{ width: '100%' }} in={!!patientId}>
                <Box w="100%">
                  <Text mb={1} color="gray.600" fontFamily="Hero-New" fontWeight={500}>
                    Move file to:
                  </Text>
                  {destTypeField ? (
                    <StandaloneInput
                      theme="detailed"
                      field={destTypeField}
                      value={destType}
                      onChange={value => {
                        setDestType(value as 'patient' | 'assessment')
                        // Reset dependent selections
                        if (value !== 'assessment') setDestAssessmentId(null)
                        setDestAccessLevel(null)
                      }}
                    />
                  ) : null}
                </Box>
              </Collapse>
            )}

            {/* Show message if patient has no assessments and assessment is selected */}
            <Collapse
              style={{ width: '100%' }}
              in={destType === 'assessment' && !hasAssessments && !loadingAssessments}>
              <Box w="100%" px={2} py={2}>
                <Text color="gray.600">
                  This patient has no assessments. Please select a different destination.
                </Text>
              </Box>
            </Collapse>

            {/* Show assessment selector when assessment is selected */}
            <Collapse
              style={{ width: '100%' }}
              in={destType === 'assessment' && hasAssessments && !loadingAssessments}>
              <Box py={1} w="100%">
                <SearchAssessments
                  noPatientPreview
                  onBack={showDestTypeSelector ? () => setDestType(null) : undefined}
                  assessmentId={destAssessmentId}
                  patientId={patientId}
                  onSubmit={async id => {
                    setDestAssessmentId(id)
                  }}
                />
              </Box>
            </Collapse>

            {/* Show access level selector */}
            <Collapse style={{ width: '100%' }} in={destType !== null && !loadingAssessments}>
              <Box py={1} w="100%">
                {accessLevelField ? (
                  <StandaloneInput
                    theme="detailed"
                    field={accessLevelField}
                    value={destAccessLevel}
                    onChange={value => setDestAccessLevel(value)}
                  />
                ) : null}
              </Box>
            </Collapse>
          </VStack>

          <CollapseError error={error} />
          <HStack justify="flex-end" w="100%">
            <Button
              onClick={onClose}
              isDisabled={submitting}
              size="sm"
              bg="white"
              border="1px solid #cdcdcd"
              fontWeight={500}
              fontFamily="Open Sans">
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              isLoading={submitting}
              size="sm"
              isDisabled={!canSubmit}
              _hover={{ bg: 'green.300' }}
              fontWeight={500}
              fontFamily="Open Sans"
              colorScheme="green">
              Move File
            </Button>
          </HStack>
        </VStack>
      )}
    />
  )
}
